import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getNewsList } from '../api';
import useAuth from '../auth/useAuth';
import ArticleIcon, { NEWS_ICON_FALLBACK } from './ArticleIcon';
import ArticleLink from './ArticleLink';

const SEEN_NEWS_KEY = 'disiLastSeenNewsId';
const SHOW_DELAY_MS = 1200;

function readLastSeenNewsId() {
  try {
    return localStorage.getItem(SEEN_NEWS_KEY);
  } catch {
    return null;
  }
}

function writeLastSeenNewsId(id) {
  try {
    localStorage.setItem(SEEN_NEWS_KEY, String(id));
  } catch {
    // Storage unavailable (private mode, quota, ...) - toast will just
    // reappear next visit, which is an acceptable fallback.
  }
}

function NewsToast() {
  const { t } = useTranslation();
  const { isAuthenticated: signedIn, user } = useAuth();
  // Until a required 2FA is set up, the platform shows nothing but the setup.
  const isAuthenticated = signedIn && !user?.twoFactorSetupRequired;
  const location = useLocation();

  const [latestNews, setLatestNews] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    let cancelled = false;

    getNewsList()
      .then((data) => {
        if (!cancelled) {
          setLatestNews((data ?? []).find((item) => !item.is_hidden) ?? null);
        }
      })
      .catch((err) => {
        console.error('Failed to load latest news:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !latestNews) {
      return undefined;
    }

    if (readLastSeenNewsId() === String(latestNews.article_id)) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIsVisible(true);
    }, SHOW_DELAY_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [isAuthenticated, latestNews]);

  const newsPath = latestNews
    ? `/news/${latestNews.article_id}`
    : null;

  // Already reading the news it's pointing to - no need to keep floating
  // over the content, close it the same way the close button would.
  useEffect(() => {
    if (isVisible && newsPath && location.pathname === newsPath) {
      writeLastSeenNewsId(latestNews.article_id);
      setIsVisible(false);

      const timer = window.setTimeout(() => setIsDismissed(true), 250);
      return () => window.clearTimeout(timer);
    }

    return undefined;
  }, [isVisible, newsPath, location.pathname, latestNews]);

  if (!isAuthenticated || !latestNews || isDismissed || !isVisible) {
    return null;
  }

  function markAsSeen() {
    writeLastSeenNewsId(latestNews.article_id);
  }

  function handleClose() {
    markAsSeen();
    setIsVisible(false);

    // Skip the collapse animation before unmounting.
    window.setTimeout(() => setIsDismissed(true), 250);
  }

  return (
    <section
      className={`news-toast${isVisible ? ' news-toast-visible' : ''}`}
      aria-labelledby="news-toast-title"
      role="status"
    >
      <div className="news-toast-header">
        <h3 id="news-toast-title">
          <i className="fa-solid fa-bullhorn" />
          <span>{t('common.companyNews')}</span>
        </h3>

        <span className="news-badge">
          {t('newsToast.newBadge')}
        </span>

        <button
          type="button"
          className="news-toast-close"
          onClick={handleClose}
          aria-label={t('newsToast.closeLabel')}
        >
          <i className="fa-solid fa-xmark" />
        </button>
      </div>

      <ArticleLink
        article={latestNews}
        className="news-card"
        onClick={markAsSeen}
      >
        <div className="news-image">
          <ArticleIcon icon={latestNews.icon} fallback={NEWS_ICON_FALLBACK} seed={latestNews.title} alt="" />
        </div>

        <div className="news-body">
          <h4>{latestNews.title}</h4>
          <p>{latestNews.summary}</p>
        </div>
      </ArticleLink>

      <ArticleLink
        article={latestNews}
        className="news-action"
        onClick={markAsSeen}
      >
        {t('newsToast.readMore')}
        <i className="fa-solid fa-arrow-right" />
      </ArticleLink>
    </section>
  );
}

export default NewsToast;
